import { ShieldAlert, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge, StatusBadge } from "@h2j-data-team/ui";
import type { AnalysisGrade } from "@repo/shared";

import { gradeTone } from "@/lib/health-format";

interface HealthGradeBadgeProps {
  grade: AnalysisGrade;
  score: number;
  /**
   * "badge"  — grade label + score, for lists and tables (default).
   * "status" — dot + grade label, for a compact header status.
   */
  variant?: "badge" | "status";
  className?: string;
}

/** The health grade of an analysis, on the library's Badge in the grade's tone. */
export function HealthGradeBadge({
  grade,
  score,
  variant = "badge",
  className,
}: HealthGradeBadgeProps) {
  const { t } = useTranslation("analyses");
  const tone = gradeTone(grade);

  if (variant === "status") {
    return (
      <StatusBadge tone={tone} className={className}>
        {t(`grade.${grade}`)}
      </StatusBadge>
    );
  }

  const Icon = tone === "success" ? ShieldCheck : ShieldAlert;
  return (
    <Badge variant={tone} className={className}>
      <Icon size={14} strokeWidth={1.8} aria-hidden className="shrink-0" />
      <span>{t(`grade.${grade}`)}</span>
      <span aria-hidden className="opacity-40">
        ·
      </span>
      <span className="font-mono tabular-nums">{score}</span>
    </Badge>
  );
}
